<template>
  <v-dialog v-model="dialog" max-width="700px" @click:outside="closed">
    <v-card :loading="loading" :disabled="loading">
      <v-card-title class="justify-center">
        <span class="text-h5">{{ $t('conglomerates.edit_agreement') }}</span>
      </v-card-title>

      <v-card-text>
        <v-container>
          <v-alert type="info" text dense class="mb-4">
            {{ $t('conglomerates.edit_agreement_hint') }}
          </v-alert>

          <v-row class="d-flex flex-column mb-4">
            <span class="body black--text my-1">{{ $t('conglomerates.agreement_name') }}*</span>
            <v-text-field
              v-model="form.name"
              filled
              flat
              dense
              :error-messages="nameErrorMessages"
              hide-details="auto"
            />
          </v-row>

          <v-row class="d-flex flex-column mb-4">
            <span class="body black--text my-1">
              {{ $t('conglomerates.agreement_total_licenses') }}*
            </span>
            <v-text-field
              v-model.number="form.totalLicenses"
              type="number"
              min="1"
              filled
              flat
              dense
              :error-messages="totalLicensesErrorMessages"
              hide-details="auto"
            />
          </v-row>

          <v-row class="d-flex flex-column mb-4">
            <span class="body black--text my-1">{{ $t('conglomerates.status') }}*</span>
            <v-select
              v-model="form.status"
              :items="statusOptions"
              item-text="label"
              item-value="value"
              filled
              flat
              dense
              hide-details="auto"
            />
          </v-row>

          <v-row class="d-flex flex-column mb-2">
            <span class="body black--text my-1">{{ $t('conglomerates.agreement_dates') }}*</span>
            <v-col>
              <v-menu
                v-model="menuStartDate"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="form.startDate"
                    :label="$t('conglomerates.start_date')"
                    prepend-icon="mdi-calendar"
                    readonly
                    :error-messages="startDateErrorMessages"
                    hide-details="auto"
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <v-date-picker v-model="form.startDate" @input="menuStartDate = false" />
              </v-menu>
            </v-col>
            <v-col>
              <v-menu
                v-model="menuEndDate"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="form.endDate"
                    :label="$t('conglomerates.end_date')"
                    prepend-icon="mdi-calendar"
                    readonly
                    :error-messages="endDateErrorMessages"
                    hide-details="auto"
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <v-date-picker v-model="form.endDate" @input="menuEndDate = false" />
              </v-menu>
            </v-col>
            <span v-if="showErrors && dateError" class="red--text caption ml-4">
              {{ $t('general.valid_dates') }}
            </span>
          </v-row>
        </v-container>
      </v-card-text>

      <v-card-actions class="px-6 pb-6">
        <v-spacer />
        <v-btn text @click="closed">{{ $t('common.cancel') }}</v-btn>
        <v-btn color="deep-purple" dark @click="save">{{ $t('common.save') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import SnackbarMixin from '@/mixins/snackbar';
import schoolArrangementService from '@/services/schoolArrangement.service';
import DateUtils from '@/utils/date.utils';

export default {
  name: 'EditArrangementModal',
  mixins: [SnackbarMixin],
  props: {
    value: { type: Boolean, default: false },
    arrangement: { type: Object, default: null },
  },
  data: () => ({
    loading: false,
    showErrors: false,
    menuStartDate: false,
    menuEndDate: false,
    form: {
      name: '',
      totalLicenses: null,
      startDate: '',
      endDate: '',
      status: 'C',
    },
  }),
  computed: {
    dialog: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    dateError() {
      if (!this.form.startDate || !this.form.endDate) return false;
      // Dates come from v-date-picker / normalized to YYYY-MM-DD, so string compare is safe.
      return this.form.endDate <= this.form.startDate;
    },
    statusOptions() {
      return [
        { value: 'C', label: this.$t('conglomerates.status_created') },
        { value: 'I', label: this.$t('conglomerates.status_inactive') },
      ];
    },
    nameErrorMessages() {
      if (!this.showErrors || this.form.name) return [];
      return [this.$t('general.is_required')];
    },
    totalLicensesErrorMessages() {
      if (!this.showErrors) return [];
      if (!this.form.totalLicenses) return [this.$t('general.is_required')];
      if (this.form.totalLicenses <= 0) return [this.$t('general.only_positive_number')];
      return [];
    },
    startDateErrorMessages() {
      if (!this.showErrors || this.form.startDate) return [];
      return [this.$t('general.is_required')];
    },
    endDateErrorMessages() {
      if (!this.showErrors || this.form.endDate) return [];
      return [this.$t('general.is_required')];
    },
  },
  watch: {
    arrangement: {
      immediate: true,
      handler(val) {
        if (val) {
          this.form = {
            name: val.name || '',
            totalLicenses: val.totalLicenses,
            startDate: this.formatDateForInput(val.startDate),
            endDate: this.formatDateForInput(val.endDate),
            status: val.status || 'C',
          };
        }
      },
    },
  },
  methods: {
    formatDateForInput(dateString) {
      return DateUtils.normalizeDateToYYYYMMDD(dateString) || '';
    },
    closed() {
      this.dialog = false;
      this.showErrors = false;
    },
    async save() {
      this.showErrors = true;

      if (
        !this.form.name
        || !this.form.totalLicenses
        || this.form.totalLicenses <= 0
        || !this.form.startDate
        || !this.form.endDate
        || this.dateError
      ) {
        this.snackbarError(this.$t('conglomerates.error_validation'));
        return;
      }

      this.loading = true;
      try {
        const res = await schoolArrangementService.update(this.arrangement.id, {
          name: this.form.name,
          totalLicenses: this.form.totalLicenses,
          startDate: this.form.startDate,
          endDate: this.form.endDate,
          status: this.form.status,
        });

        if (res?.status === 'OK') {
          this.snackbarSuccess(this.$t('conglomerates.save_success'));
          this.$emit('updated');
          this.closed();
        } else {
          this.snackbarError(this.$t('conglomerates.error_saving'));
        }
      } catch (error) {
        this.snackbarError(this.$t('conglomerates.error_saving'));
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
